/**
 * What a place has received, read from the API báo cáo page (docs/specs/reporting-api.md §12):
 * "Lịch sử" (calls and errors, never the data), "Dòng chưa nhận được" (rows set aside),
 * "Đối chiếu số liệu" (totals to compare) and "Báo ngay khi có thay đổi" (signal addresses).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { api } from '../../../api';
import type { ReportingCall, ReportingConsumer, ReportingParkedRow, ReportingSignal } from '../../../api/areas/reporting';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Field, Pager, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { EmptyState, Loading } from '../../../components/States';
import { useWrite, WriteError } from '../../../components/Write';
import { formatCount, formatDateTime, formatRelative } from '../../../lib/format';
import { callResult, datasetsOf, isCallError, suspendReason, type CallResult } from '../../../lib/reportingApi';
import { useApi } from '../../../lib/useApi';
import { FormFooter, type DialogBodyProps } from '../../operator/shared';
import { useScopeLabels } from './dialogs';

const PAGE = 25;

const RESULT_TONE: Partial<Record<CallResult, Tone>> = {
  OK: 'ok',
  RATE_LIMITED: 'pending',
  UNAVAILABLE: 'warn',
  INVALID: 'warn',
};

const EVENT_KEYS = ['reporting_key.suspend', 'reporting_key.resume', 'reporting_checkpoint.reset', 'reporting_key.rotate', 'reporting_key.revoke', 'reporting_consumer.change'] as const;

/** "Lịch sử": tiles, the calls of the last 30 days (errors only on demand) and the events. */
export function HistoryView({ consumer, close }: { consumer: ReportingConsumer; close: () => void }) {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  const usage = useApi((signal) => api.getReportingUsage({ consumer_id: consumer.id, limit: 500 }, { signal }), [consumer.id]);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [page, setPage] = useState(0);
  if (usage.status === 'loading') return <Loading rows={4} />;
  if (usage.status === 'error') return <DataError error={usage.error} onRetry={usage.reload} />;
  const u = usage.data;
  const calls = onlyErrors ? u.calls.filter(isCallError) : u.calls;
  const pages = Math.max(1, Math.ceil(calls.length / PAGE));
  const shown = calls.slice(page * PAGE, page * PAGE + PAGE);
  const columns: Column<ReportingCall>[] = [
    { key: 'at', header: t('history.col.at'), className: 'whitespace-nowrap', cell: (c) => <span title={formatDateTime(c.at)}>{formatRelative(c.at)}</span> },
    { key: 'data', header: t('history.col.data'), cell: (c) => (c.dataset ? labels.dataset(c.dataset) : t('history.other')) },
    { key: 'rows', header: t('history.col.rows'), numeric: true, cell: (c) => formatCount(c.rows) },
    { key: 'contacts', header: t('history.col.contacts'), cell: (c) => (c.contacts ? t('yes') : t('no')) },
    {
      key: 'result',
      header: t('history.col.result'),
      cell: (c) => {
        const r = callResult(c);
        return (
          <Badge tone={RESULT_TONE[r] ?? 'crit'} icon={false}>
            {t(`result.${r}`)}
          </Badge>
        );
      },
    },
    { key: 'from', header: t('history.col.from'), cell: (c) => <span className="font-mono text-xs">{c.client_ip ?? '—'}</span> },
    { key: 'key', header: t('history.col.key'), cell: (c) => <span className="font-mono text-xs">{c.key_prefix}…</span> },
  ];
  return (
    <div className="grid min-w-0 gap-4 [&>*]:min-w-0" data-reporting-history>
      <StatGrid className="xl:grid-cols-3">
        <StatCard label={t('history.calls24h')} value={formatCount(u.calls_24h)} />
        <StatCard label={t('history.rowsToday')} value={formatCount(u.rows_today)} hint={u.budget_today ? t('history.budget', { budget: formatCount(u.budget_today) }) : undefined} />
        <StatCard label={t('history.errors24h')} value={formatCount(u.errors_24h)} tone={u.errors_24h > 0 ? 'warn' : 'neutral'} />
      </StatGrid>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
          checked={onlyErrors}
          onCheckedChange={(v) => {
            setOnlyErrors(v === true);
            setPage(0);
          }}
          data-field="only-errors"
        />
        {t('history.onlyErrors')}
      </label>
      <div className="rounded-lg border">
        <DataTable columns={columns} rows={shown} rowKey={(c) => `${c.at}/${c.key_prefix}/${c.route}/${c.status}`} empty={<EmptyState title={onlyErrors ? t('history.noErrors') : t('history.empty')} />} />
        {calls.length > PAGE ? (
          <Pager canPrevious={page > 0} canNext={page + 1 < pages} onPrevious={() => setPage((p) => p - 1)} onNext={() => setPage((p) => p + 1)} info={t('history.page', { page: page + 1, pages })} />
        ) : null}
      </div>
      {u.events.length ? (
        <div className="grid gap-2">
          <h3 className="text-sm font-semibold">{t('history.events')}</h3>
          <ul className="grid gap-1 text-sm" data-reporting-events>
            {u.events.map((e) => {
              const known = (EVENT_KEYS as readonly string[]).includes(e.action) ? (e.action as (typeof EVENT_KEYS)[number]) : null;
              const reason = e.action === 'reporting_key.suspend' ? t(`suspended.${suspendReason(e.reason)}`) : e.reason;
              return (
                <li key={`${e.at}/${e.action}`} className="flex flex-wrap gap-x-2">
                  <span className="text-muted-foreground" title={formatDateTime(e.at)}>
                    {formatRelative(e.at)}
                  </span>
                  <span className="font-medium">{known ? t(`history.event.${known}`, { reason }) : t('history.event.other')}</span>
                  {reason && e.action !== 'reporting_key.suspend' ? <span className="text-muted-foreground">· {reason}</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">{t('history.note')}</p>
      <FormFooter busy={false} onClose={close} />
    </div>
  );
}

/** "Dòng chưa nhận được": rows set aside, each with "Gửi lại". */
export function ParkedView({ consumer, canManage, close, changed }: DialogBodyProps & { consumer: ReportingConsumer; canManage: boolean }) {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  const rows = useApi((signal) => api.listReportingParked(consumer.id, { signal }), [consumer.id]);
  const [resent, setResent] = useState<string[]>([]);
  const resend = useWrite((r: ReportingParkedRow, opts) => api.resendReportingParked(consumer.id, r.dataset, r.object_id, opts));
  const columns: Column<ReportingParkedRow>[] = [
    { key: 'data', header: t('parked.col.data'), cell: (r) => labels.dataset(r.dataset) },
    { key: 'record', header: t('parked.col.record'), className: 'whitespace-normal', cell: (r) => <span className="font-medium">{r.label}</span> },
    { key: 'attempts', header: t('parked.col.attempts'), numeric: true, cell: (r) => formatCount(r.attempts) },
    { key: 'last', header: t('parked.col.last'), cell: (r) => <span title={formatDateTime(r.parked_at)}>{formatRelative(r.parked_at)}</span> },
    {
      key: 'resend',
      header: '',
      cell: (r) =>
        resent.includes(`${r.dataset}/${r.object_id}`) ? (
          <span className="text-xs text-muted-foreground">{t('parked.resent')}</span>
        ) : canManage ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={resend.busy}
            onClick={() =>
              void resend.run(r).then((ok) => {
                if (ok) {
                  setResent((cur) => [...cur, `${r.dataset}/${r.object_id}`]);
                  changed();
                }
              })
            }
          >
            {t('parked.resend')}
          </Button>
        ) : null,
    },
  ];
  return (
    <div className="grid min-w-0 gap-4 [&>*]:min-w-0" data-reporting-parked>
      <Callout tone="info">{t('parked.help', { name: consumer.name })}</Callout>
      {rows.status === 'loading' ? <Loading rows={2} /> : null}
      {rows.status === 'error' ? <DataError error={rows.error} onRetry={rows.reload} /> : null}
      {rows.status === 'success' ? (
        <div className="rounded-lg border">
          <DataTable columns={columns} rows={rows.data.items} rowKey={(r) => `${r.dataset}/${r.object_id}`} empty={<EmptyState title={t('parked.empty')} />} />
        </div>
      ) : null}
      {resend.state.phase === 'error' ? <WriteError error={resend.state.error} /> : null}
      <FormFooter busy={false} onClose={close} />
    </div>
  );
}

/** "Đối chiếu số liệu": rows, totals per currency and counts by status of each kind of data. */
export function ReconcileView({ consumer, close }: { consumer: ReportingConsumer; close: () => void }) {
  const { t, i18n } = useTranslation('reporting');
  const labels = useScopeLabels();
  const data = useApi((signal) => api.getReportingReconciliation(consumer.id, { signal }), [consumer.id]);
  // Field labels come from the catalogue (Vietnamese); English shows the field's own name.
  const catalogue = useApi((signal) => api.getReportingCatalogue({ signal }), []);
  const fieldLabel = (dataset: string, field: string) => {
    if (i18n.language.startsWith('vi') && catalogue.status === 'success') {
      const f = catalogue.data.datasets.find((d) => d.name === dataset)?.fields.find((x) => x.name === field);
      if (f?.label_vi) return f.label_vi;
    }
    return field.replace(/_minor$/, '').replace(/_/g, ' ');
  };
  return (
    <div className="grid min-w-0 gap-4 [&>*]:min-w-0" data-reporting-reconcile>
      <Callout tone="info">{t('reconcile.help', { name: consumer.name })}</Callout>
      {data.status === 'loading' ? <Loading rows={3} /> : null}
      {data.status === 'error' ? <DataError error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'success' && data.data.items.length === 0 ? <EmptyState title={t('reconcile.empty')} /> : null}
      {data.status === 'success'
        ? data.data.items.map((d) => (
            <section key={d.dataset} className="grid gap-2 rounded-lg border p-3" data-dataset={d.dataset}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold">{labels.dataset(d.dataset)}</h3>
                <span className="text-sm">
                  {t('reconcile.rows')}: <span className="tabular font-medium">{formatCount(d.rows)}</span>
                </span>
              </div>
              {d.money.map((m) => (
                <div key={String(m.currency)} className="grid gap-1 text-sm">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t('reconcile.total')} ({String(m.currency)})
                  </span>
                  <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
                    {Object.entries(m)
                      .filter(([k]) => k !== 'currency')
                      .map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3">
                          <dt className="text-muted-foreground">{fieldLabel(d.dataset, k)}</dt>
                          <dd className="tabular">{typeof v === 'number' ? formatCount(v) : (v ?? '—')}</dd>
                        </div>
                      ))}
                  </dl>
                </div>
              ))}
              {d.by_status.length ? (
                <div className="grid gap-1 text-sm">
                  <span className="text-xs font-medium text-muted-foreground">{t('reconcile.byStatus')}</span>
                  <div className="flex flex-wrap gap-2">
                    {d.by_status.map((s) => (
                      <span key={s.status ?? '-'} className="rounded-md border px-2 py-0.5 text-xs">
                        <code>{s.status ?? '—'}</code> <span className="tabular font-medium">{formatCount(s.count)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          ))
        : null}
      <FormFooter busy={false} onClose={close} />
    </div>
  );
}

function SignalRow({ consumer, dataset, signal, canManage, changed }: { consumer: ReportingConsumer; dataset: string; signal: ReportingSignal | undefined; canManage: boolean; changed: () => void }) {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState('');
  const [current, setCurrent] = useState(signal);
  const save = useWrite((u: string, opts) => api.setReportingSignal(consumer.id, dataset, { url: u }, opts));
  const remove = useWrite((_: null, opts) => api.removeReportingSignal(consumer.id, dataset, opts));
  return (
    <div className="grid gap-2 rounded-md border p-3" data-signal={dataset}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">{labels.dataset(dataset)}</span>
        {current && !editing ? (
          <span className="flex flex-wrap items-center gap-2">
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{current.endpoint.url}</code>
            {current.endpoint.consecutive_failures > 0 ? <Badge tone="warn">{t('signals.failing', { count: current.endpoint.consecutive_failures })}</Badge> : null}
          </span>
        ) : !editing ? (
          <span className="text-xs text-muted-foreground">{t('signals.none')}</span>
        ) : null}
      </div>
      {canManage && editing ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save.run(url.trim()).then((r) => {
              if (r) {
                setCurrent(r);
                setEditing(false);
                setUrl('');
                changed();
              }
            });
          }}
        >
          <Field label={t('signals.field')} className="min-w-64 flex-1">
            <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t('signals.placeholder')} autoComplete="off" required />
          </Field>
          <Button type="submit" size="sm" disabled={save.busy || !url.trim()}>
            {t('signals.save')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
            {t('close')}
          </Button>
        </form>
      ) : canManage ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setEditing(true)}>
            {current ? t('signals.change') : t('signals.save')}
          </Button>
          {current ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={remove.busy}
              onClick={() =>
                void remove.run(null).then((r) => {
                  if (r) {
                    setCurrent(undefined);
                    changed();
                  }
                })
              }
            >
              {t('signals.remove')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {save.state.phase === 'error' ? <WriteError error={save.state.error} /> : null}
      {remove.state.phase === 'error' ? <WriteError error={remove.state.error} /> : null}
    </div>
  );
}

/** "Báo ngay khi có thay đổi": one signal address per kind of data the place receives. */
export function SignalsView({ consumer, signals, canManage, close, changed }: DialogBodyProps & { consumer: ReportingConsumer; signals: ReportingSignal[]; canManage: boolean }) {
  const { t } = useTranslation('reporting');
  const datasets = datasetsOf(consumer.datasets);
  return (
    <div className="grid min-w-0 gap-3 [&>*]:min-w-0" data-reporting-signals>
      <Callout tone="info">{t('signals.help', { name: consumer.name })}</Callout>
      <Callout tone="warn">{t('signals.secret', { name: consumer.name })}</Callout>
      {datasets.length === 0 ? <EmptyState title={t('signals.empty')} /> : null}
      {datasets.map((d) => (
        <SignalRow key={d} consumer={consumer} dataset={d} signal={signals.find((s) => s.dataset === d)} canManage={canManage} changed={changed} />
      ))}
      <FormFooter busy={false} onClose={close} />
    </div>
  );
}
